import { useCallback } from 'react';

import { useActivityBuilderContext } from 'approot/activity-builder/activity-builder.context';
import { ComponentEmptyButton } from '../shared/component-parts';
import { EditProps } from './ebook.constants';
import { useBatchComponentUpdateContext } from '../shared/batch-component-update.context';
import { ComponentDimensions } from '../shared/component-height/component-dimensions';
import DocumentEditPreview from '../document/document-edit-preview';
import { EBOOK_PDF } from 'global/constants';
import { getStimulusLabel } from '../shared/logic/get-stimulus-label';
import {
  getClosestImageUrl,
  ImageDisplayQuality,
} from 'approot/shared/images/imgix-responsive';
import { ComponentAbsoluteWrapper } from '../shared/component-height/component-dimensions.style';
import { useImageDimensionEffect } from 'approot/shared/images/images.hooks';
import { getSmallestImage } from '../shared/logic/get-smallest-image';

const EbookEdit = (props: EditProps) => {
  const { sectionId, id, properties } = props;

  const { openDrawer, activeComponent } = useActivityBuilderContext();
  const { updateComponentProperties } = useBatchComponentUpdateContext();

  const { imageDimensions, setContainerElement } = useImageDimensionEffect({
    imageUrl: getSmallestImage(
      props.stimulusResourceProperties?.xlCoverImageUrls || []
    ),
  });

  const handleHeightUpdate = useCallback(
    (width: number, height: number) => {
      const dimensionData = {
        ...properties.dimensionData,
        width,
        height: Math.floor(height),
      };
      updateComponentProperties({
        input: {
          sectionId,
          componentId: id,
          componentData: {
            properties: {
              dimensionData,
            },
          },
        },
        properties: {
          ...properties,
          dimensionData,
        },
        typename: 'ActivitiesEbookComponent',
      });
    },
    [id, sectionId, properties, updateComponentProperties]
  );

  return props.stimulusResourceProperties ? (
    <ComponentDimensions
      id={id}
      showHandle={activeComponent?.id === props.id}
      defaultValue={properties.dimensionData}
      onResizeEnd={handleHeightUpdate}
    >
      {props.stimulusResourceProperties?.documentKey ? (
        <ComponentAbsoluteWrapper ref={setContainerElement}>
          <DocumentEditPreview
            documentKey={props.stimulusResourceProperties.documentKey}
            name={props.stimulusResourceProperties.resourceName}
            description={props.stimulusResourceProperties.description}
            badgeName={getStimulusLabel(EBOOK_PDF)}
            type={EBOOK_PDF}
            link={props.stimulusResourceProperties.link}
            showDetails={false} // todo: will be redesigned
            coverImage={
              props.stimulusResourceProperties.xlCoverImageUrls?.length
                ? getClosestImageUrl({
                    imageUrls:
                      props.stimulusResourceProperties.xlCoverImageUrls,
                    containerSize: imageDimensions?.width
                      ? Math.max(imageDimensions.width, imageDimensions.height)
                      : window.innerWidth,
                    quality: ImageDisplayQuality.High,
                  })
                : undefined
            }
            thumbnail={
              props.stimulusResourceProperties.coverImage?.original || undefined
            }
          />
        </ComponentAbsoluteWrapper>
      ) : null}
    </ComponentDimensions>
  ) : (
    <>
      <ComponentEmptyButton
        iconId="e-book"
        onClick={() => openDrawer({ componentId: props.id })}
        buttonText={`Add ${getStimulusLabel(EBOOK_PDF)}`}
      />
    </>
  );
};

export default EbookEdit;
